import { createSlice } from "@reduxjs/toolkit";
import { getAllUsers, createNewUser, updateUser, deleteUser, toggleUserStatus, getActiveUsers, getUserProfile, } from "../thunk/user-thunk";
const initialState = {
    users: null,
    activeUsers: null,
    activeUsersLoading: false,
    activeUsersError: null,
    paginationInfo: null,
    selectedUser: null,
    loading: false,
    error: null,
    createLoading: false,
    createError: null,
    updateLoading: false,
    updateError: null,
    deleteLoading: false,
    deleteError: null,
    toggleStatusLoading: false,
    toggleStatusError: null,
    selectedUserRow: null,
    userFilters: {
        userName: undefined,
        userId: undefined,
        roleIds: [],
        lastLoginAt: undefined,
        accessPointUids: [],
        isActive: null,
        globalSearch: undefined,
    },
    currentPageNumber: 1,
    currentPageSize: 25,
    sortBy: null,
    sortOrder: null,
    userProfile: null,
    userProfileLoading: false,
    userProfileError: null,
};
const usersSlice = createSlice({
    name: "users",
    initialState,
    reducers: {
        setSelectedUser: (state, action) => {
            state.selectedUser = action.payload;
        },
        clearError: (state) => {
            state.error = null;
            state.createError = null;
            state.updateError = null;
            state.deleteError = null;
            state.toggleStatusError = null;
        },
        setSelectedUserRow: (state, action) => {
            state.selectedUserRow = action.payload;
        },
        resetSelectedUserRow: (state) => {
            state.selectedUserRow = null;
        },
        clearUsers: (state) => {
            state.users = null;
            state.paginationInfo = null;
            state.selectedUser = null;
        },
        updateFilters: (state, action) => {
            // Check if filters are actually changing
            const oldFilters = state.userFilters;
            const newFilters = Object.assign(Object.assign({}, state.userFilters), action.payload);
            // Compare if anything actually changed
            let hasChanged = false;
            for (const key in action.payload) {
                if (JSON.stringify(oldFilters[key]) !==
                    JSON.stringify(newFilters[key])) {
                    hasChanged = true;
                    break;
                }
            }
            state.userFilters = newFilters;
            // Only reset to first page if filters actually changed
            if (hasChanged) {
                state.currentPageNumber = 1;
            }
        },
        setPagination: (state, action) => {
            state.currentPageNumber = action.payload.pageNumber;
            state.currentPageSize = action.payload.pageSize;
        },
        setPageNumber: (state, action) => {
            state.currentPageNumber = action.payload;
        },
        setPageSize: (state, action) => {
            state.currentPageSize = action.payload;
        },
        setFiltersFromForm: (state, action) => {
            state.userFilters = action.payload;
        },
        setGlobalSearch: (state, action) => {
            state.userFilters.globalSearch = action.payload;
            state.currentPageNumber = 1;
        },
        setSorting: (state, action) => {
            state.sortBy = action.payload.sortBy;
            state.sortOrder = action.payload.sortOrder;
        },
        resetFilters: (state) => {
            state.userFilters = {
                userName: undefined,
                userId: undefined,
                roleIds: [],
                lastLoginAt: undefined,
                accessPointUids: [],
                isActive: undefined,
                globalSearch: undefined,
            };
            state.currentPageNumber = 1;
        },
        resetFiltersAndPagination: (state) => {
            state.userFilters = {
                userName: undefined,
                userId: undefined,
                roleIds: [],
                lastLoginAt: undefined,
                accessPointUids: [],
                isActive: null,
                globalSearch: undefined,
            };
            state.currentPageNumber = 1;
            state.currentPageSize = 25;
            state.sortBy = null;
            state.sortOrder = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Get All Users
            .addCase(getAllUsers.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(getAllUsers.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.users = action.payload.items;
            state.paginationInfo = {
                totalCount: action.payload.totalCount,
                pageNumber: action.payload.pageNumber,
                pageSize: action.payload.pageSize,
                totalPages: action.payload.totalPages,
                hasPreviousPage: action.payload.hasPreviousPage,
                hasNextPage: action.payload.hasNextPage,
            };
        })
            .addCase(getAllUsers.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.users = null;
            state.paginationInfo = null;
        })
            // Create New User
            .addCase(createNewUser.pending, (state) => {
            state.createLoading = true;
            state.createError = null;
        })
            .addCase(createNewUser.fulfilled, (state, action) => {
            state.createLoading = false;
            state.createError = null;
            if (state.users) {
                state.users.push(action.payload.data);
            }
        })
            .addCase(createNewUser.rejected, (state, action) => {
            state.createLoading = false;
            state.createError = action.payload;
        })
            // Update User
            .addCase(updateUser.pending, (state) => {
            state.updateLoading = true;
            state.updateError = null;
        })
            .addCase(updateUser.fulfilled, (state, action) => {
            state.updateLoading = false;
            state.updateError = null;
            if (state.users) {
                const userIndex = state.users.findIndex((user) => user.id === action.payload.id);
                if (userIndex !== -1) {
                    state.users[userIndex] = action.payload;
                }
            }
            state.selectedUserRow = null;
        })
            .addCase(updateUser.rejected, (state, action) => {
            state.updateLoading = false;
            state.updateError = action.payload;
        })
            // Delete User
            .addCase(deleteUser.pending, (state) => {
            state.deleteLoading = true;
            state.deleteError = null;
        })
            .addCase(deleteUser.fulfilled, (state, action) => {
            state.deleteLoading = false;
            state.deleteError = null;
            if (state.users) {
                state.users = state.users.filter((user) => user.id !== action.payload.id);
            }
            if (state.paginationInfo) {
                state.paginationInfo.totalCount -= 1;
            }
        })
            .addCase(deleteUser.rejected, (state, action) => {
            state.deleteLoading = false;
            state.deleteError = action.payload;
        })
            // Toggle User Status
            .addCase(toggleUserStatus.pending, (state) => {
            state.toggleStatusLoading = true;
            state.toggleStatusError = null;
        })
            .addCase(toggleUserStatus.fulfilled, (state, action) => {
            state.toggleStatusLoading = false;
            state.toggleStatusError = null;
            if (state.users) {
                const userIndex = state.users.findIndex((user) => user.id === action.payload.id);
                if (userIndex !== -1) {
                    state.users[userIndex].isActive = action.payload.isActive;
                }
            }
        })
            .addCase(toggleUserStatus.rejected, (state, action) => {
            state.toggleStatusLoading = false;
            state.toggleStatusError = action.payload;
        })
            // Get Active Users for dropdown
            .addCase(getActiveUsers.pending, (state) => {
            state.activeUsersLoading = true;
            state.activeUsersError = null;
        })
            .addCase(getActiveUsers.fulfilled, (state, action) => {
            state.activeUsersLoading = false;
            state.activeUsersError = null;
            state.activeUsers = action.payload;
        })
            .addCase(getActiveUsers.rejected, (state, action) => {
            state.activeUsersLoading = false;
            state.activeUsersError = action.payload;
            state.activeUsers = null;
        })
            // Get User Profile
            .addCase(getUserProfile.pending, (state) => {
            state.userProfileLoading = true;
            state.userProfileError = null;
        })
            .addCase(getUserProfile.fulfilled, (state, action) => {
            state.userProfileLoading = false;
            state.userProfileError = null;
            state.userProfile = action.payload;
        })
            .addCase(getUserProfile.rejected, (state, action) => {
            state.userProfileLoading = false;
            state.userProfileError = action.payload;
            state.userProfile = null;
        });
    },
});
export const { setSelectedUser, clearError, clearUsers, updateFilters, setPagination, setPageNumber, setPageSize, setFiltersFromForm, setGlobalSearch, setSorting, resetFilters, resetFiltersAndPagination, setSelectedUserRow, resetSelectedUserRow, } = usersSlice.actions;
export default usersSlice.reducer;
